import { Fragment } from "react";
import StatusPill from "shared/StatusPill";
import { cn } from "lib/utils";
import type { Channel, ConversationMessage } from "../api/OutreachTouchesApi";
import { formatCalendarDate, formatKarachiClock, karachiDateOf } from "../outreachDates";

const CHANNEL_LABEL: Record<Channel, string> = { email: "Email", linkedin: "LinkedIn" };
/** What we sent through the LinkedIn step, copied into the thread when the conversation starts (source touch_<kind>). */
const TOUCH_LABEL: Record<string, string> = {
  touch_connection_request: "connection request note", touch_acceptance_message: "message after they accepted", touch_follow_up: "follow-up",
};

/** A conversation read like a chat: theirs on the left, ours on the right, a divider for each Karachi day. Newest message first. Read-only. */
export default function ConversationThread({ messages, who, onSkipAnalysis, busy }: {
  messages: ConversationMessage[]; who: string;
  /** Mark a reply of theirs "nothing to learn from it" (what /rfp-prime-reply-analysis would otherwise pick up). Without it the button is not shown. */
  onSkipAnalysis?: (messageId: number) => void; busy?: boolean;
}) {
  if (messages.length === 0) return <p className="text-sm text-muted-foreground">No message has been logged yet.</p>;
  let lastDay = "";
  return (
    <ol className="space-y-3" aria-label="Messages">
      {[...messages].reverse().map((m) => {
        const day = m.at ? karachiDateOf(m.at) : "";
        const divider = day && day !== lastDay;
        if (day) lastDay = day;
        const ours = m.sender === "us";
        return (
          <Fragment key={m.id}>
            {divider && (
              <li role="separator" aria-label={formatCalendarDate(day)} className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" aria-hidden />{formatCalendarDate(day)}<span className="h-px flex-1 bg-border" aria-hidden />
              </li>
            )}
            <li data-testid={`message-${m.id}`} data-side={ours ? "us" : "them"} className={cn("flex", ours ? "justify-end" : "justify-start")}>
              <div className={cn("max-w-[85%] rounded-2xl border px-3.5 py-2.5 text-sm", ours ? "bg-primary-soft border-primary/30 rounded-br-sm" : "bg-surface-muted border-border rounded-bl-sm")}>
                <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{ours ? "Us" : who}</span>
                  <StatusPill tone="neutral" size="sm">{CHANNEL_LABEL[m.channel]}</StatusPill>
                  {m.at && <span>{formatKarachiClock(m.at)}</span>}
                  {m.source === "backfill" && <span title="Logged before conversations were tracked">· imported from the old reply log</span>}
                  {TOUCH_LABEL[m.source] && <span title="Sent from the LinkedIn step, copied here so the thread starts with what we sent">· {TOUCH_LABEL[m.source]}</span>}
                  {!ours && m.analysis_status === "pending" && (
                    <>
                      <StatusPill tone="info" size="sm" title="Run /rfp-prime-reply-analysis to learn from this reply">Analysis pending</StatusPill>
                      {onSkipAnalysis && (
                        <button type="button" disabled={busy} onClick={() => onSkipAnalysis(m.id)} className="underline underline-offset-2 disabled:opacity-50">Nothing to analyse</button>
                      )}
                    </>
                  )}
                  {!ours && m.analysis_status === "needs_context" && <StatusPill tone="warning" size="sm">Needs more context</StatusPill>}
                  {!ours && m.analysis_status === "done" && <StatusPill tone="success" size="sm">Analysed</StatusPill>}
                  {!ours && m.analysis_status === "skipped" && <StatusPill tone="neutral" size="sm">Nothing to analyse</StatusPill>}
                </div>
                <p className="whitespace-pre-wrap break-words text-foreground">{m.text}</p>
              </div>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}
